import { Typography, Box, Paper } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { MESSAGES } from '../Services/MessagesTexts';
import OfferMarket from '../Models/OfferMarket'
import { marketOpenDates } from '../Services/DateService';

interface MarketDetailsProps {
  offerMarket: OfferMarket;
}

/**
 * `MarketDetails` is a React functional component that displays information about an offer market.
 * It receives a single prop, `offerMarket`, which is an object with details about a market's status and rules.
 * The component uses responsive design to adjust text sizes for different devices and provides a
 * message about the market status, which could be open, closed, or displaying results.
 * It also shows the minimum offer required for participation if applicable.
 */
const MarketDetails: React.FC<MarketDetailsProps> = ({ offerMarket }) => {

  const { validFrom, validTo, minimumOffer } = offerMarket;

  let marketStatusMessage = "";
  if (!offerMarket.isClosed && validFrom && validTo) {
    marketStatusMessage = marketOpenDates(validFrom, validTo);
  } else {
    marketStatusMessage = MESSAGES.MARKET_CLOSED;
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', gap: 2, borderLeft: '4px solid', borderLeftColor: offerMarket.isClosed ? 'divider' : 'secondary.main' }}>
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignSelf: 'flex-start', p: 1.5, bgcolor: 'primary.light', color: 'primary.main', borderRadius: 2 }}><StorefrontOutlinedIcon /></Box>
      <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        {marketStatusMessage}
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        {
          !offerMarket.isClosed
            ? MESSAGES.OFFER_MARKET_RESULTS
            : MESSAGES.RESULTS_AVAILABLE_ONLINE
        }
      </Typography>

      {
        (!offerMarket.isClosed) && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            {
              minimumOffer === 0 || minimumOffer === null
                ? MESSAGES.NO_MINIMUM_OFFER
                : `${MESSAGES.MINIMUM_OFFER} ${minimumOffer}%`
            }
          </Typography>
        )
      }
      </Box>
    </Paper>
  );
};

export default MarketDetails;
